import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Watermark from '@/components/Watermark';
import { useWatermark } from '@/context/WatermarkContext';
import { ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.startsWith('/uploads')) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.includes('/uploads/')) {
    const uploadsPath = url.substring(url.indexOf('/uploads'));
    return `${BACKEND_URL}/api${uploadsPath}`;
  }
  return url;
};

export const KittenCard = ({ kitten }) => {
  const { priceVisible } = useWatermark();
  
  const getStatusStyles = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Reserved':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Sold':
        return 'bg-stone-100 text-stone-500 border-stone-200';
      default:
        return 'bg-stone-100 text-stone-600 border-stone-200';
    }
  };

  // Calculate age from date of birth or use provided age
  const getAge = () => {
    if (kitten.age) return kitten.age;
    if (kitten.dateOfBirth) {
      const birth = new Date(kitten.dateOfBirth);
      const now = new Date();
      const months = Math.floor((now - birth) / (1000 * 60 * 60 * 24 * 30));
      if (months < 1) return 'Newborn';
      return `${months} month${months > 1 ? 's' : ''} old`;
    }
    return '';
  };

  return (
    <div
      data-testid={`kitten-card-${kitten.id}`}
      className="kitten-card bg-white rounded-3xl overflow-hidden border border-rose-100/50 shadow-sm hover:shadow-xl transition-all duration-300"
    >
      <Link to={`/kittens/${kitten.id}`}>
        <div className="aspect-[4/5] overflow-hidden relative">
          <img
            src={normalizeImageUrl(kitten.images?.[0]) || 'https://images.unsplash.com/photo-1628113268359-034ec254a8e4?w=500'}
            alt={kitten.name}
            className="kitten-image w-full h-full object-cover"
          />
          <Watermark size="sm" />
          {/* Status Badge Overlay */}
          <div className="absolute top-4 left-4">
            <Badge className={`${getStatusStyles(kitten.status)} border px-3 py-1 text-xs font-medium rounded-full`}>
              {kitten.status}
            </Badge>
          </div>
        </div>
      </Link>
      <div className="p-5">
        <Link to={`/kittens/${kitten.id}`}>
          <h3 className="text-xl font-serif font-semibold text-stone-800 mb-1">{kitten.name}</h3>
          <p className="text-sm text-stone-500 mb-3">
            {kitten.breed}
          </p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {kitten.gender && (
              <span className="text-xs bg-rose-50 text-rose-600 px-2.5 py-1 rounded-full">
                {kitten.gender}
              </span>
            )}
            {getAge() && (
              <span className="text-xs bg-stone-100 text-stone-600 px-2.5 py-1 rounded-full">
                {getAge()}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-rose-50">
            {kitten.status !== 'Sold' && priceVisible && kitten.price ? (
              <p className="text-xl font-serif font-bold text-[#E85D75]">${kitten.price?.toLocaleString()}</p>
            ) : (
              <p className="text-sm text-stone-400">Contact for price</p>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-[#E85D75] hover:bg-rose-50 rounded-full px-4"
            >
              View <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default KittenCard;
